import React from "react"

import { useConnectivityRecovery } from "@/contexts/connectivity-recovery-context"

interface AppVersionUpdateContextValue {
  dismissUpdatePrompt: () => void
  isUpdateAvailable: boolean
  reloadToLatestVersion: () => Promise<void>
}

interface AppVersionUpdateProviderProps {
  children: React.ReactNode
}

const AppVersionUpdateContext =
  React.createContext<AppVersionUpdateContextValue>({
    dismissUpdatePrompt: () => undefined,
    isUpdateAvailable: false,
    reloadToLatestVersion: () => Promise.resolve(undefined),
  })

const VERSION_CHECK_INTERVAL_MS = 5 * 60_000
const VERSION_CHECK_MIN_GAP_MS = 15_000
const VERSION_ENDPOINT = "/version.json"

export const unregisterServiceWorkers = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) {
    return
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations.map((registration) => registration.unregister())
    )
  } catch {
    // Fail silently and continue normal app operation.
  }
}

export const reloadPage = () => {
  try {
    globalThis.location.reload()
  } catch (error: unknown) {
    if (
      error instanceof Error &&
      error.message.includes("Not implemented: navigation")
    ) {
      return
    }
    throw error
  }
}

export const AppVersionUpdateProvider: React.FC<
  AppVersionUpdateProviderProps
> = ({ children }) => {
  const { isRecoveryActive } = useConnectivityRecovery()
  const isE2EForcedUpdatePrompt = React.useMemo(() => {
    const searchParams = new URLSearchParams(globalThis.location.search)
    return searchParams.get("e2eShowUpdatePrompt") === "1"
  }, [])
  const [isUpdateAvailable, setIsUpdateAvailable] = React.useState(
    isE2EForcedUpdatePrompt
  )
  const dismissedVersionRef = React.useRef<string | null>(null)
  const latestRemoteVersionRef = React.useRef<string | null>(null)
  const lastCheckAtMsRef = React.useRef(0)
  const inFlightCheckRef = React.useRef<Promise<void> | null>(null)

  React.useEffect(() => {
    if (isE2EForcedUpdatePrompt) {
      return
    }

    let isEffectCancelled = false
    const handleFocus = () => {
      void checkForVersionUpdate()
    }

    const evaluatePromptForVersion = (remoteVersion: string) => {
      latestRemoteVersionRef.current = remoteVersion
      const localVersion = process.env.APP_VERSION
      const hasNewVersion = Boolean(
        localVersion && remoteVersion !== localVersion
      )
      const isDismissedForCurrentVersion =
        dismissedVersionRef.current === remoteVersion
      setIsUpdateAvailable(hasNewVersion && !isDismissedForCurrentVersion)
    }

    const checkForVersionUpdate = async (force = false): Promise<void> => {
      if (isEffectCancelled) {
        return
      }

      const inFlightCheck = inFlightCheckRef.current
      if (inFlightCheck) {
        await inFlightCheck
        return
      }

      const now = Date.now()
      const isWithinThrottleWindow =
        now - lastCheckAtMsRef.current < VERSION_CHECK_MIN_GAP_MS
      if (!force && isWithinThrottleWindow) {
        return
      }

      const checkPromise = (async () => {
        lastCheckAtMsRef.current = Date.now()
        try {
          const response = await globalThis.fetch(VERSION_ENDPOINT, {
            cache: "no-cache",
            headers: {
              Accept: "application/json",
            },
          })
          if (!response.ok) {
            return
          }
          const body: unknown = await response.json()
          const remoteVersion =
            typeof body === "object" &&
            body !== null &&
            "version" in body &&
            typeof body.version === "string"
              ? body.version
              : null
          if (!remoteVersion || isEffectCancelled) {
            return
          }
          evaluatePromptForVersion(remoteVersion)
        } catch {
          // Fail silently and retry on next trigger.
        }
      })()

      inFlightCheckRef.current = checkPromise
      try {
        await checkPromise
      } finally {
        if (inFlightCheckRef.current === checkPromise) {
          inFlightCheckRef.current = null
        }
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForVersionUpdate()
      }
    }

    void unregisterServiceWorkers()

    void checkForVersionUpdate(true)

    const updateCheckIntervalId = globalThis.setInterval(() => {
      void checkForVersionUpdate()
    }, VERSION_CHECK_INTERVAL_MS)
    globalThis.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      isEffectCancelled = true
      globalThis.clearInterval(updateCheckIntervalId)
      globalThis.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      inFlightCheckRef.current = null
    }
  }, [isE2EForcedUpdatePrompt])

  const dismissUpdatePrompt = React.useCallback(() => {
    dismissedVersionRef.current = latestRemoteVersionRef.current
    setIsUpdateAvailable(false)
  }, [])

  const reloadToLatestVersion = React.useCallback((): Promise<void> => {
    setIsUpdateAvailable(false)
    reloadPage()
    return Promise.resolve()
  }, [])

  const value = React.useMemo(
    () => ({
      dismissUpdatePrompt,
      isUpdateAvailable: isUpdateAvailable && !isRecoveryActive,
      reloadToLatestVersion,
    }),
    [
      dismissUpdatePrompt,
      isRecoveryActive,
      isUpdateAvailable,
      reloadToLatestVersion,
    ]
  )

  return (
    <AppVersionUpdateContext value={value}>{children}</AppVersionUpdateContext>
  )
}

export const useAppVersionUpdate = () => React.use(AppVersionUpdateContext)
